<template>
  <div
    class="card"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div class="card-header"></div>
    <div class="card-body message-body-wrapper pb-0" ref="messageBody">
      <!-- 交互表示 -->
      <div v-for="(message, index) in messages" :key="index" class="mb-4">
        <!-- ユーザーのメッセージ（右）-->
        <div
          v-if="message.is_user"
          class="d-flex justify-content-end pe-3 mb-3"
        >
          <div class="message-wrapper">
            <div class="user-message-bubble">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p
                class="mb-0 text-sm text-dark"
                v-html="parseMarkdown(message.text)"
              ></p>
            </div>
          </div>
        </div>

        <!-- AIのメッセージ（左）-->
        <div
          v-else
          class="d-flex justify-content-start mb-3"
          :class="{ 'message-preparing-answer': message.is_preparing }"
        >
          <div class="input-wrapper">
            <div class="bg-white border rounded p-3 shadow-sm">
              <div v-if="!message.text" class="d-flex align-items-center">
                <div class="spinner-grow text-dark me-2" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <span v-if="this.$store.state.isMobileOpen">
                  ただいま回答準備中です。
                </span>
                <span v-else class="w-100">
                  <template
                    v-if="
                      selectedModel.includes('deep-research') ||
                      message.is_deep_research
                    "
                  >
                    現在、詳細な調査と分析を行っています。<br />
                    通常より時間がかかりますが、結果の精度を高めています。<br />
                    <strong>完了まで10〜45分ほどお待ちください。</strong>
                    <!-- 進捗バー -->
                    <!-- <div class="progress-container position-relative pt-3 pb-2">
                      <div
                        class="progress-bar bg-gradient-warning"
                        :style="{ width: message.progress + '%' }"
                      ></div>
                    </div>
                    <p class="small">処理状況：{{ message.progressText }}</p>
                    -->
                  </template>
                  <template v-else>
                    ただいま回答準備中です。少々お待ちください。
                  </template>
                </span>
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                class="mb-2 text-dark"
                v-html="parseMarkdown(message.text)"
              ></div>
              <div v-if="message.text" class="d-flex justify-content-end gap-2">
                <div class="input-group input-group-sm" style="width: auto">
                  <!-- 左：ファイルタイプ選択 -->
                  <select
                    v-model="selectedTypes[message.id]"
                    class="form-select text-secondary"
                    style="max-width: 100px"
                  >
                    <option value="">形式</option>
                    <option value="pptx">pptx</option>
                    <option value="xlsx">Excel</option>
                    <option value="docx">Word</option>
                  </select>

                  <!-- 右：ダウンロードボタン -->
                  <button
                    class="btn btn-outline-secondary gap-1 mb-0"
                    type="button"
                    @click="handleOfficeFileDownload(message.id)"
                  >
                    <i class="fas fa-download"></i>
                    ダウンロード
                  </button>
                </div>
                <button
                  v-if="message.text"
                  class="btn btn-sm btn-outline-secondary px-2 mb-0"
                  type="button"
                  @click="copyToClipboard(message.text)"
                >
                  <i class="fas fa-copy" style="font-size: 0.75rem"></i>
                  コピーする
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 入力欄 -->
    <div
      class="position-sticky bottom-0 p-3 pt-2 mt-5 px-md-5 d-flex justify-content-center"
    >
      <div class="w-100 d-flex justify-content-center">
        <div
          class="input-group shadow-lg input-text-wrapper input-wrapper-layout align-items-end"
          :class="{ dragging: isDragging }"
        >
          <template v-if="isDeepResearchRunning">
            <div class="w-100 text-center text-muted small py-2">
              このチャットルームは Deep Research
              実行中のため、完了まで入力をお待ちください。他のチャットルームは利用可能です。
            </div>
          </template>
          <template v-else>
            <div class="file-preview-area" v-if="attachedFiles.length > 0">
              <div class="file-list-wrap">
                <div
                  class="file-item-wrap"
                  v-for="(file, index) in attachedFiles"
                  :key="index"
                >
                  <div class="file-content">
                    <template v-if="isImageFile(file)">
                      <img
                        class="file-image-preview"
                        :src="getFilePreview(file)"
                        :alt="file.name"
                      />
                    </template>
                    <template v-else>
                      <i class="fas fa-paperclip me-2 text-secondary"></i>
                      <span class="file-name">{{ file.name }}</span>
                    </template>
                  </div>
                  <button
                    class="btn-remove"
                    @click="removeFile(index)"
                    title="削除"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 選択モデル -->
            <div
              v-if="selectedModel"
              @click.stop="toggleToolMenu"
              class="selected-model-container"
            >
              <label class="selected-model-chip">
                言語モデル：{{ selectedModelLabel }}
              </label>
            </div>

            <!-- モデル選択メニュー -->
            <div v-if="showToolMenu" class="tool-menu">
              <div class="tool-menu-title">言語モデル</div>

              <div
                v-for="model in selectedModelOptions"
                :key="model.label"
                class="tool-menu-item"
                @click="selectModel(model)"
              >
                {{ model.label }}
              </div>
            </div>

            <div class="input-left-tools">
              <!-- +ボタン -->
              <label
                v-if="false"
                class="btn-plus mb-0"
                @click.stop="toggleToolMenu"
              >
                <i class="fas fa-plus" style="font-size: 18px; color: #555"></i>
              </label>
              <!-- ファイル添付ボタン -->
              <label
                class="mb-0"
                for="file-upload"
                style="cursor: pointer; margin-right: 8px"
              >
                <i
                  class="fas fa-paperclip"
                  style="font-size: 18px; color: #555"
                ></i>
              </label>
            </div>

            <input
              type="file"
              id="file-upload"
              ref="fileInput"
              multiple
              @change="handleInputFileChange"
              style="display: none"
              accept=".pdf,.csv,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            />
            <!-- テキストエリア -->
            <textarea
              :class="[
                'form-control',
                { 'border-0': true, 'shadow-none': true, expanded: focused },
              ]"
              placeholder="気軽に質問してね！"
              @focus="focused = true"
              @blur="focused = false"
              v-model="userInput"
              @keydown.enter.exact.prevent="submitMessage"
              @keydown.enter.ctrl="insertNewLine"
              @paste="handlePaste"
              maxlength="7000"
            ></textarea>
            <!-- Web検索 -->
            <div class="chat-tools-floating">
              <label class="web-search-toggle">
                <i class="fas fa-globe-asia me-1"></i>
                <span>Web検索</span>
                <input
                  type="checkbox"
                  v-model="isWebSearch"
                  @change="notifyWebSearch"
                />
                <span class="toggle-slider"></span>
              </label>
            </div>
            <!-- 送信ボタン/停止ボタン -->
            <div
              class="btn btn-icon-only btn-icon-send text-white rounded-circle"
              :class="isStreaming ? 'bg-secondary' : 'bg-dark'"
              @click="handleSubmitOrStop"
            >
              <i v-if="!isStreaming" class="far fa-comment-dots"></i>
              <i v-else class="fas fa-stop"></i>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
//import ChatSelectGroup from '@/components/PageLayout/App/ChatSelectGroup.vue'
import HTTPService from '@/services/HTTPService'

export default {
  name: 'ChatCard',
  components: {
    //ChatSelectGroup,
  },
  props: {
    messages: {
      type: Array,
      required: true,
    },
    parseMarkdown: {
      type: Function,
      required: true,
    },
    attachedFiles: {
      type: Array,
      required: true,
    },
    model: {
      type: String,
      default: '',
    },
    modelLabel: {
      type: String,
      default: '',
    },
    modelOptions: {
      type: Array,
      required: true,
    },
    personality: {
      type: String,
      default: '',
    },
    webSearch: {
      type: Boolean,
      default: false,
    },
    isHistoryLoaded: {
      type: Boolean,
      default: false,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'handle-dragover',
    'handle-drop',
    'handle-office-file-download',
    'handle-selected-model',
    'handle-selected-model-label',
    'handle-selected-reasoning-effort',
    'handle-selected-personality',
    'handle-selected-personality-label',
    'handel-web-search',
    'input-message',
    'upload-files',
    'remove-file',
    'stop-stream',
  ],
  data() {
    return {
      userInput: '',
      focused: false,
      isDragging: false,
      isPreparing: false,
      isWebSearch: this.webSearch,
      dragCounter: 0,
      selectedModel: this.model,
      selectedModelLabel: this.modelLabel,
      selectedModelOptions: this.modelOptions,
      selectedPersonality: this.personality,
      selectedPersonalityLabel: '',
      selectedTypes: {},
      showToolMenu: false,
    }
  },
  methods: {
    handleOfficeFileDownload(messageId) {
      const type = this.selectedTypes[messageId]

      if (!type) {
        alert('形式を選択してください')
        return
      }

      this.$emit('handle-office-file-download', messageId, type)
    },
    handleSubmitOrStop() {
      if (this.isStreaming) {
        this.$emit('stop-stream')
      } else {
        this.submitMessage()
      }
    },
    toggleToolMenu() {
      this.showToolMenu = !this.showToolMenu
    },
    selectModel(model) {
      this.showToolMenu = false
      this.selectedModel = model.value
      this.selectedModelLabel = model.label
      this.$emit('handle-selected-model', model.value)
      this.$emit('handle-selected-model-label', model.label)
      this.$emit('handle-selected-reasoning-effort', model.effort ?? null)
    },
    handleDragEnter() {
      this.dragCounter++
      this.isDragging = true
    },
    handleDragLeave() {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
      }
    },
    handleDrop(event) {
      this.dragCounter = 0
      this.isDragging = false
      this.$emit('handle-drop', event)
      this.$refs.fileInput.value = ''
    },
    handleDragOver(event) {
      this.$emit('handle-dragover', event)
      event.preventDefault()
    },
    handleInputFileChange(event) {
      this.$emit('upload-files', event)
      this.$refs.fileInput.value = ''
    },
    handlePaste(event) {
      const items = event.clipboardData?.items
      if (!items) return

      const files = []

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault()

          const file = item.getAsFile()
          if (!file) continue

          const ext = file.type.split('/')[1] || 'png'
          const fileName = `screenshot-${Date.now()}.${ext}`

          const newFile = new File([file], fileName, {
            type: file.type,
          })

          files.push(newFile)
        }
      }

      if (files.length === 0) return

      // ここがポイント：input[type=file]風のイベントを作る
      const pseudoEvent = {
        target: {
          files: files,
        },
      }

      // 既存ロジックに流す
      this.handleInputFileChange(pseudoEvent)
    },
    removeFile(index) {
      this.$emit('remove-file', index)
    },
    isImageFile(file) {
      return file.type.startsWith('image/')
    },
    getFilePreview(file) {
      return URL.createObjectURL(file)
    },
    getFileExtension(file) {
      return file.name.split('.').pop().toUpperCase()
    },
    sanitize(html) {
      return DOMPurify.sanitize(html)
    },
    submitMessage() {
      const trimmed = this.userInput.trim()
      if (trimmed) {
        this.$emit('send-message', trimmed)
        this.userInput = ''
      }
    },
    insertNewLine(event) {
      const textarea = event.target
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.userInput =
        this.userInput.substring(0, start) +
        '\n' +
        this.userInput.substring(end)
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1
      })
    },
    copyToClipboard(text) {
      const processedText = text.replace(/\\n/g, '\n')
      navigator.clipboard.writeText(processedText)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageBody
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    startFakeProgress(message) {
      // すでに動いてたら何もしない
      if (message._timer) return

      const steps = [
        { limit: 20, text: '🧠 情報を分析しています...' },
        { limit: 50, text: '🔍 関連情報を収集しています...' },
        { limit: 80, text: '📊 データを整理しています...' },
        { limit: 95, text: '✍️ レポートを作成しています...' },
      ]

      const totalDuration = 15 * 60 * 1000
      const intervalMs = 800
      const totalSteps = totalDuration / intervalMs
      const incrementBase = 95 / totalSteps

      // タイマー保存
      message._timer = setInterval(() => {
        if (message.progress < 95) {
          const fluctuation = incrementBase * 0.82 * Math.random()
          message.progress += fluctuation

          if (message.progress > 95) message.progress = 95
        }

        const step = steps.find((s) => message.progress < s.limit)
        if (step) message.progressText = step.text
      }, intervalMs)
    },
    async fetchLinkPreview() {
      const cards = document.querySelectorAll('.source-card')

      for (const card of cards) {
        const url = card.getAttribute('href')
        console.log('url:' + url)

        const response = await HTTPService.link_preview(url)
        const data = response.data

        const titleEl = card.querySelector('.source-title')
        const descEl = card.querySelector('.source-description')

        if (titleEl) {
          titleEl.textContent = data.title
        }

        if (descEl) {
          descEl.textContent = data.description
        }
      }
    },
    notifyWebSearch() {
      this.$emit('handel-web-search', this.isWebSearch)
    },
  },
  watch: {
    messages: {
      async handler(newMessages) {
        // Vueの描画完了を待つ
        await this.$nextTick()

        if (this.isPreparingAnswer) {
          this.scrollToBottom()
        }
        if (this.isHistoryLoaded) {
          this.scrollToBottom()
        }

        newMessages.forEach((m) => {
          if (!(m.id in this.selectedTypes)) {
            this.selectedTypes[m.id] = ''
          }
        })

        const last = newMessages[newMessages.length - 1]

        if (
          last &&
          !last.is_user &&
          !last.text &&
          this.selectedModel.includes('deep-research')
        ) {
          this.startFakeProgress(last)
        }

        // 画面の描画が完了したタイミングで、
        // Web検索URLの記事タイトル、ディスクリプションを取得する
        //if (last !== undefined && last.is_preparing === false) {
        //  this.fetchLinkPreview()
        //}
      },
      deep: true,
      immediate: true,
    },
    selectedModel(newVal) {
      this.$emit('handle-selected-model', newVal)
    },
    selectedModelLabel(newVal) {
      this.$emit('handle-selected-model-label', newVal)
    },
    selectedPersonality(newVal) {
      this.$emit('handle-selected-personality', newVal)
    },
    selectedPersonalityLabel(newVal) {
      this.$emit('handle-selected-personality-label', newVal)
    },
  },
  computed: {
    isPreparingAnswer() {
      return this.messages.some((m) => !m.is_user && !m.text)
    },
    isDeepResearchRunning() {
      return this.messages.some((m) => m.is_deep_research === true)
    },
  },
  mounted() {
    this.scrollToBottom()
  },
}
</script>
<style scoped></style>
